// Excel експортът на дневника.
//
// Изнася се това, което е на екрана — същите филтри, същата подредба — но за
// целия период, не само текущата страница. Числата излизат като ЧИСЛА:
// латентност в клетка като „3151 ms" убива всяко сортиране в Excel.

import dayjs from "dayjs";
import type { TFunction } from "i18next";

import {
  fetchAuditLogs,
  type AuditLogEntry,
  type AuditLogListParams,
} from "../../../../api/auditLogs";
import { exportToExcel, type ExcelColumn } from "../../../../utils/excelExport";
import {
  impactLabelKey,
  moduleLabelKey,
  outcomeLabelKey,
  outcomeOf,
} from "./auditTaxonomy";

/** Таванът на `page_size` в `GET /audit-logs` (`le=500`). */
const EXPORT_PAGE_SIZE = 500;
/** Предпазител срещу безкрайно листене при неочакван `total`. */
const MAX_EXPORT_PAGES = 40;

export const fetchAuditLogsForExport = async (
  params: AuditLogListParams,
  totalRows: number,
): Promise<AuditLogEntry[]> => {
  const pages = Math.min(
    MAX_EXPORT_PAGES,
    Math.max(1, Math.ceil(totalRows / EXPORT_PAGE_SIZE)),
  );
  const rows: AuditLogEntry[] = [];
  for (let page = 1; page <= pages; page += 1) {
    const response = await fetchAuditLogs({
      ...params,
      page,
      page_size: EXPORT_PAGE_SIZE,
    });
    rows.push(...response.items);
    if (response.items.length < EXPORT_PAGE_SIZE) break;
  }
  return rows;
};

const columnsFor = (t: TFunction): ExcelColumn<AuditLogEntry>[] => [
  {
    header: t("auditLog.col.timestamp"),
    value: (row) => dayjs(row.timestamp).format("DD.MM.YYYY HH:mm:ss"),
    width: 20,
  },
  {
    header: t("auditLog.col.outcome"),
    value: (row) => t(outcomeLabelKey(outcomeOf(row))),
    width: 16,
  },
  {
    header: t("auditLog.col.operation"),
    value: (row) => row.operation,
    width: 34,
  },
  {
    header: t("auditLog.col.module"),
    value: (row) => t(moduleLabelKey(row.module)),
    width: 16,
  },
  {
    header: t("auditLog.col.impact"),
    value: (row) => t(impactLabelKey(row.impact)),
    width: 14,
  },
  { header: t("auditLog.col.table"), value: (row) => row.table_name ?? "", width: 10 },
  { header: t("auditLog.col.billId"), value: (row) => row.bill_id ?? "", width: 10 },
  { header: t("auditLog.col.user"), value: (row) => row.user_name ?? "", width: 20 },
  { header: t("auditLog.col.userId"), value: (row) => row.user_id ?? "", width: 8 },
  { header: t("auditLog.col.method"), value: (row) => row.method, width: 10 },
  { header: t("auditLog.col.status"), value: (row) => row.status_code, width: 9 },
  {
    header: t("auditLog.col.duration"),
    value: (row) => row.duration_ms,
    width: 12,
  },
  { header: t("auditLog.col.path"), value: (row) => row.path, width: 46 },
  { header: t("auditLog.col.entryId"), value: (row) => row.id, width: 10 },
];

export const exportAuditLogs = (rows: AuditLogEntry[], t: TFunction) => {
  exportToExcel({
    // БЕЗ собствен времеви печат — `exportToExcel` вече лепи `_${stamp}`.
    filename: t("auditLog.title"),
    sheetName: t("auditLog.title"),
    columns: columnsFor(t),
    rows,
  });
};
